import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    getDossierHistory,
    getDossierVersion,
    restoreDossierVersion,
    formatActionLabel,
    formatChangeSummary,
    formatRelativeTime,
    HistoryEntry
} from '../../services/dossierHistoryService';
import { IDossier } from './widgets/types';

interface DossierHistoryDropdownProps {
    isOpen: boolean;
    onClose: () => void;
    dossierId: string;
    userRole: string | null;
    /**
     * Appelé après une restauration réussie. Reçoit le dossier
     * complet renvoyé par l'API : le caller peut le passer à
     * replaceDossier() au lieu de recharger la page.
     */
    onRestoreComplete?: (restoredDossier?: IDossier) => void;
    onVersionPreview?: (versionNumber: number, snapshot: any) => void;
}

const DossierHistoryDropdown: React.FC<DossierHistoryDropdownProps> = ({
    isOpen,
    onClose,
    dossierId,
    userRole,
    onRestoreComplete,
    onVersionPreview
}) => {
    const [entries, setEntries] = useState<HistoryEntry[]>([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [isRestoring, setIsRestoring] = useState<number | null>(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const canRestore = userRole === 'author' || userRole === 'admin';
    const canPreview = onVersionPreview !== undefined;

    const loadHistory = useCallback(async (page: number, append = false) => {
        if (append) {
            setIsLoadingMore(true);
        } else {
            setIsLoading(true);
        }
        setError(null);

        const result = await getDossierHistory(dossierId, page, 10);

        if (result) {
            if (append) {
                setEntries(prev => [...prev, ...result.entries]);
            } else {
                setEntries(result.entries);
            }
            setTotal(result.total);
            setCurrentPage(page);
            setHasMore(page < result.pages);
        } else {
            setError('Impossible de charger l\'historique');
        }

        setIsLoading(false);
        setIsLoadingMore(false);
    }, [dossierId]);

    // Handler de scroll pour l'infinite scroll
    const handleScroll = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container || isLoadingMore || !hasMore || isLoading) return;

        const { scrollTop, scrollHeight, clientHeight } = container;
        const threshold = 50;

        if (scrollHeight - scrollTop - clientHeight < threshold) {
            loadHistory(currentPage + 1, true);
        }
    }, [isLoadingMore, hasMore, isLoading, currentPage, loadHistory]);

    // Chargement initial et reset
    useEffect(() => {
        if (isOpen) {
            setEntries([]);
            setCurrentPage(1);
            setHasMore(true);
            loadHistory(1);
        }
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Attacher le listener de scroll
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    const handleRestore = async (versionNumber: number) => {
        if (!canRestore) return;

        const confirmed = window.confirm(
            `Restaurer la version ${versionNumber} ?`
        );

        if (!confirmed) return;

        setIsRestoring(versionNumber);

        const result = await restoreDossierVersion(dossierId, versionNumber);

        if (result?.success) {
            onRestoreComplete?.(result.dossier as IDossier | undefined);
            onClose();
        } else {
            setError('Impossible de restaurer');
        }

        setIsRestoring(null);
    };

    const handlePreview = async (versionNumber: number) => {
        if (!canPreview) return;

        setIsLoadingPreview(versionNumber);
        setError(null);

        const result = await getDossierVersion(dossierId, versionNumber);

        if (result?.version?.snapshot) {
            onVersionPreview?.(versionNumber, result.version.snapshot);
            onClose();
        } else {
            setError('Impossible de charger la version');
        }

        setIsLoadingPreview(null);
    };

    const getActionColor = (action: HistoryEntry['action']): string => {
        const colors: Record<HistoryEntry['action'], string> = {
            created: 'bg-mcdm-green-100',
            content_updated: 'bg-mcdm-primary-500',
            settings_updated: 'bg-purple-500',
            author_added: 'bg-teal-500',
            author_removed: 'bg-orange-500',
            author_changed: 'bg-indigo-500',
            restored: 'bg-amber-500',
            deleted: 'bg-red-500'
        };
        return colors[action] || 'bg-gray-500';
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                ref={dropdownRef}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="absolute right-0 mt-2 w-80 bg-white dark:bg-mcdm-third-500 rounded-lg shadow-lg z-50 overflow-hidden"
            >
                {/* Header */}
                <div className="px-4 py-3 border-b border-gray-200 dark:border-mcdm-third-400">
                    <h3 className="text-sm font-medium text-mcdm-third-500 dark:text-white">
                        Historique {total > 0 && <span className="text-gray-400">({total})</span>}
                    </h3>
                </div>

                {/* Error */}
                {error && (
                    <div className="px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs">
                        {error}
                    </div>
                )}

                {/* Loading */}
                {isLoading && (
                    <div className="flex justify-center items-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-mcdm-secondary-400"></div>
                    </div>
                )}

                {/* Empty state */}
                {!isLoading && entries.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
                        Aucun historique
                    </div>
                )}

                {/* Timeline */}
                {!isLoading && entries.length > 0 && (
                    <div ref={scrollContainerRef} className="max-h-80 overflow-y-auto custom-scrollbar">
                        {entries.map((entry, index) => {
                            const changeSummaryParts = formatChangeSummary(entry.changeSummary);
                            const isCurrent = index === 0;

                            return (
                                <div
                                    key={entry._id}
                                    className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-mcdm-third-400 border-b border-gray-100 dark:border-mcdm-third-400 last:border-b-0"
                                >
                                    {/* Version + Action */}
                                    <div className="flex items-center justify-between mb-1">
                                        <div className="flex items-center gap-2">
                                            <span className={`w-2 h-2 rounded-full ${getActionColor(entry.action)}`}></span>
                                            <span className="text-xs font-medium text-mcdm-third-500 dark:text-white">
                                                v{entry.versionNumber}
                                            </span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                {formatActionLabel(entry.action)}
                                            </span>
                                        </div>

                                        {/* Current badge or action buttons */}
                                        {isCurrent ? (
                                            <span className="px-2 py-0.5 text-xs rounded-full bg-mcdm-green-300 text-white">
                                                Actuelle
                                            </span>
                                        ) : (
                                            entry.action !== 'deleted' && (
                                                <div className="flex items-center gap-1">
                                                    {canPreview && (
                                                        <button
                                                            onClick={() => handlePreview(entry.versionNumber)}
                                                            disabled={isLoadingPreview !== null || isRestoring !== null}
                                                            className={`px-2 py-0.5 text-xs rounded transition-colors ${
                                                                isLoadingPreview === entry.versionNumber
                                                                    ? 'bg-gray-300 dark:bg-mcdm-primary-100 cursor-wait'
                                                                    : 'border border-mcdm-third-300 bg-mcdm-primary-100 dark:bg-mcdm-primary-100 dark:border-mcdm-third-300 text-mcdm-third-500 dark:text-mcdm-third-500 hover:bg-mcdm-primary-300 dark:hover:bg-mcdm-primary-400'
                                                            }`}
                                                        >
                                                            {isLoadingPreview === entry.versionNumber ? '...' : 'Voir'}
                                                        </button>
                                                    )}
                                                    {canRestore && (
                                                        <button
                                                            onClick={() => handleRestore(entry.versionNumber)}
                                                            disabled={isRestoring !== null || isLoadingPreview !== null}
                                                            className={`px-2 py-0.5 text-xs rounded transition-colors ${
                                                                isRestoring === entry.versionNumber
                                                                    ? 'bg-mcdm-third-300 dark:bg-primary-200 cursor-wait'
                                                                    : 'border border-mcdm-third-300 dark:border-mcdm-third-300 bg-mcdm-secondary-400 hover:bg-mcdm-secondary-500 text-white'
                                                            }`}
                                                        >
                                                            {isRestoring === entry.versionNumber ? '...' : 'Restaurer'}
                                                        </button>
                                                    )}
                                                </div>
                                            )
                                        )}
                                    </div>

                                    {/* Time */}
                                    <div className="text-xs text-gray-400 dark:text-gray-500 mb-1">
                                        {formatRelativeTime(entry.createdAt)}
                                    </div>

                                    {/* Changes */}
                                    {changeSummaryParts.length > 0 && (
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                            {changeSummaryParts.join(' • ')}
                                        </div>
                                    )}

                                    {/* User */}
                                    <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                                        {entry.userId?.profilePicture ? (
                                            <img
                                                src={entry.userId.profilePicture}
                                                alt=""
                                                className="w-4 h-4 rounded-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-4 h-4 rounded-full bg-mcdm-secondary-400 flex items-center justify-center text-white text-[10px]">
                                                {entry.userId?.firstName?.[0] || '?'}
                                            </div>
                                        )}
                                        <span>
                                            {entry.userId?.firstName} {entry.userId?.lastName?.[0]}.
                                        </span>
                                    </div>
                                </div>
                            );
                        })}

                        {/* Loader infinite scroll */}
                        {isLoadingMore && (
                            <div className="flex justify-center py-3">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-mcdm-secondary-400"></div>
                            </div>
                        )}
                    </div>
                )}
            </motion.div>
        </AnimatePresence>
    );
};

export default DossierHistoryDropdown;
